import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom, Observable } from "rxjs";
import { PaginatedEntityApiService } from "../../../../shared/store/pagination/paginated-entity-api.service";
import { environment } from "../../../../../environments/environment";
import { Chargement } from "../../models/chargement.model";
import { Palette } from "../../../palette/models/palette.model";
import { TypePdf } from "../../../documents/enums/type-pdf.enum";
import { printPdf } from "../../../../shared/utils/print-pdf.utils";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

@Injectable({ providedIn: "root" })
export class ChargementApiService implements PaginatedEntityApiService<Chargement> {
  loadOne(id: string): Observable<Chargement> {
    return this.httpClient.get<Chargement>(
      `${environment.api()}/chargements/id/${id}`,
    );
  }

  httpClient = inject(HttpClient);
  dialogService = inject(DialogsService);

  create(entity: Chargement): Observable<Chargement> {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements`,
      entity,
    );
  }

  createNew(entity: Chargement): Observable<Chargement> {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/new`,
      entity,
    );
  }

  addPalettes(
    palettes: Palette[],
    chargementId: string,
  ): Observable<Chargement> {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/add_palettes/`,
      {
        palettes,
        chargementId,
      },
    );
  }

  removePalette(paletteId: string) {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/remove_from_chargement/paletteId/${paletteId}`,
      null,
    );
  }

  pret(chargement: Chargement) {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/pret`,
      chargement,
    );
  }

  cancel(id: string) {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/${id}/cancel`,
      null,
    );
  }

  remove(id: string): Observable<string> {
    throw new Error("Method not implemented.");
  }

  update(entity: Chargement): Observable<Chargement> {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements`,
      entity,
    );
  }

  loadAll(): Observable<Chargement[]> {
    return this.httpClient.get<Chargement[]>(
      `${environment.api()}/chargements/not_charged`,
    );
  }

  loadPage(
    page: number,
    limit: number,
    query?: any,
  ): Observable<{ data: Chargement[]; total: number }> {
    let params = new HttpParams()
      .set("page", String(page))
      .set("pageSize", String(limit));
    if (query?.search) params = params.set("search", query.search);
    if (query?.minDate) params = params.set("minDate", query.minDate);
    if (query?.maxDate) params = params.set("maxDate", query.maxDate);
    const appendArr = (key: string, arr?: string[]) => {
      if (arr?.length) arr.forEach((v) => (params = params.append(key, v)));
    };
    appendArr("etat", query?.etat);
    appendArr("transporteurIds", query?.transporteurIds);
    appendArr("entrepotSourceIds", query?.entrepotSourceIds);
    return this.httpClient.get<{ data: Chargement[]; total: number }>(
      `${environment.api()}/chargements/search`,
      { params },
    );
  }

  loadAllCharged(): Observable<Chargement[]> {
    return this.httpClient.get<Chargement[]>(
      `${environment.api()}/chargements/charged`,
    );
  }

  /**
   * Liste légère pour le calendrier de planning. Endpoint dédié qui ne
   * remonte que `id, numero, date, etat, transporteurDenomination,
   * entrepotSourceDenomination` — pas de palettes/stocks/expéditions.
   * Remplace l'usage de `loadAll()` (heavy) sur la page Expéditions.
   */
  loadPlanning(): Observable<
    Array<{
      id: string;
      numero: string | null;
      date: string | Date | null;
      etat: string;
      transporteurDenomination: string | null;
      entrepotSourceDenomination: string | null;
    }>
  > {
    return this.httpClient.get<any>(`${environment.api()}/chargements/planning`);
  }

  /**
   * Options pour les filtres (transporteurs + entrepôts source) — DISTINCT
   * côté DB plutôt que reconstruites à partir d'une liste complète des
   * chargements en mémoire.
   */
  loadFilterOptions(): Observable<{
    transporteurs: { id: string; denomination: string }[];
    entrepots: { id: string; denomination: string }[];
  }> {
    return this.httpClient.get<{
      transporteurs: { id: string; denomination: string }[];
      entrepots: { id: string; denomination: string }[];
    }>(`${environment.api()}/chargements/filter-options`);
  }

  /** Supprime un chargement vide (refusé si BL/BT actifs ou état CHARGE). */
  deleteEmpty(id: string): Observable<{ deleted: true }> {
    return this.httpClient.post<{ deleted: true }>(
      `${environment.api()}/chargements/${id}/delete`,
      {},
    );
  }

  declareAllPalettesReady(chargementId: string): Observable<Chargement> {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/declare_all_ready`,
      { chargementId },
    );
  }

  saveExpeditionState(chargementId: string, expeditionState: Record<string, any>) {
    return this.httpClient.post<void>(
      `${environment.api()}/chargements/save_expedition_state`,
      { chargementId, expeditionState },
    );
  }

  expedierChargement(c: Chargement) {
    return this.httpClient.post<Chargement>(
      `${environment.api()}/chargements/expedier/`,
      { c },
    );
  }

  loadBlPayload(blId: string) {
    return this.httpClient.get(
      `${environment.api()}/bonLivraisons/payload/${blId}`,
    );
  }

  loadBlsPayloads(payload: { blIds: string[] }) {
    return this.httpClient.post(
      `${environment.api()}/bonLivraisons/payload/multiple`,
      payload,
    );
  }

  loadPalettesPayloads(payload: { ids: string[] }) {
    return this.httpClient.post(
      `${environment.api()}/palette/payload/multiple/`,
      payload,
    );
  }

  async printPdfBL(payload, documentTitle?: string) {
    await this.dialogService.openPdfLoader(async () => {
      const res = await firstValueFrom(
        this.httpClient.post(
          `${environment.pdfApi()}/pdf/${TypePdf.BL_CLIENT}`,
          payload,
          {
            responseType: "blob",
          },
        ),
      );
      const file = new Blob([res], { type: "application/pdf" });
      printPdf(URL.createObjectURL(file), documentTitle || 'BL');
    });
  }

  loadBtsPayloads(payload: { btIds: string[] }) {
    return this.httpClient.post(
      `${environment.api()}/bonTransferts/payload/multiple`,
      payload,
    );
  }

  async printPdfBT(payload, documentTitle?: string) {
    await this.dialogService.openPdfLoader(async () => {
      const res = await firstValueFrom(
        this.httpClient.post(
          `${environment.pdfApi()}/pdf/${TypePdf.BON_TRANSFERT}`,
          payload,
          {
            responseType: "blob",
          },
        ),
      );
      const file = new Blob([res], { type: "application/pdf" });
      printPdf(URL.createObjectURL(file), documentTitle || 'BT');
    });
  }

  async printPdfFichePalette(payload, documentTitle?: string) {
    await this.dialogService.openPdfLoader(async () => {
      const res = await firstValueFrom(
        this.httpClient.post(
          `${environment.pdfApi()}/pdf/${TypePdf.FICHE_PALETTE_EXPEDITION}`,
          payload,
          {
            responseType: "blob",
          },
        ),
      );
      const file = new Blob([res], { type: "application/pdf" });
      printPdf(URL.createObjectURL(file), documentTitle || 'Fiche Palette');
    });
  }
}
